/**
 * Wave 3 · B7 — v1 memory management.
 *
 * useTabVisible() reports whether the CALLING component's tab is the one the
 * operator is actually looking at right now — both dimensions have to be
 * true:
 *   1. it's the ACTIVE pill in the bottom tab bar (TabActiveContext, set by
 *      TabsHost per mounted tab — an inactive tab is kept mounted but hidden
 *      via display:none per the keep-alive design), AND
 *   2. the browser TAB itself is visible (document.visibilityState) — a
 *      background browser tab shouldn't poll even if it "owns" the active
 *      pill.
 *
 * Call sites gate their polling/SSE on this instead of the previous
 * `document.visibilityState === 'visible'` one-off checks (CommandCenter,
 * Suggestions) — same behavior when there's only one record open, and now
 * ALSO pauses when a different record's tab is focused.
 *
 * Components rendered outside the tab system (no TabsHost ancestor — none
 * today, but this keeps the hook safe to use standalone / in tests) default
 * TabActiveContext to `true`, so behavior degrades to "just watch
 * document.visibilityState", never to "always paused".
 */
import { createContext, useContext, useEffect, useState } from 'react'

export const TabActiveContext = createContext(true)

function isDocVisible(): boolean {
  return typeof document === 'undefined' || document.visibilityState === 'visible'
}

export function useTabVisible(): boolean {
  const isActiveTab = useContext(TabActiveContext)
  const [docVisible, setDocVisible] = useState(isDocVisible)

  useEffect(() => {
    const onChange = () => setDocVisible(isDocVisible())
    document.addEventListener('visibilitychange', onChange)
    return () => document.removeEventListener('visibilitychange', onChange)
  }, [])

  return isActiveTab && docVisible
}
